import store from '@/store'
import uuidv4 from 'uuid'
import i18n from '@/utils/i18n'
import _isObject from 'lodash/isObject'
import {ActivePluginList, InterestList} from '@/utils/constant'

export const FilterTypes = {
  BRAND: 1,
  VENDOR: 2,
  BRANCH: 3,
  TAG: 4,
  CATEGORY: 5,
  TAX: 6,
  STAFF: 7,
  PAYMENT_TYPE: 8,
  CUSTOM: 9, // User provides the custom option list for the dropdowns
  VERY_CUSTOM: 10, // User provides the whole html for the filter via slot
  TERMINAL: 11,
  BRANCH_TREE: 12,
  SALES_CHANNEL: 13,
  MARKETPLACE: 14,
  UNIT: 15,
  TOGGLE_BOX: 16,
  REGISTRATION_SOURCE: 17,
  ACTIVE_PLUGIN: 18,
  INTEREST: 19
}

export class Filter {
  constructor(data = {}) {
    this.uuid = data.uuid || uuidv4()
    this.type = data.type
    this.value = data.value || [] // selected id list
    this.searchKeyword = data.searchKeyword

    // Defaults
    this.idKey = data.idKey || "id"
    this.labelKey = data.labelKey || "name"
    this.selectKey = data.selectKey || "selected"
    this.disabledKey = data.disabledKey || "disabled"

    // Custom optionals
    this.customLabel = data.customLabel || null
    this.customDataList = data.customDataList || null
    this.customTerminalDataList = data.customTerminalDataList || null
    this.customIdentifier = data.customIdentifier || null
    this.backendKey = data.backendKey || null
    
    // Extra
    this.isOpen = data.isOpen || false
    this.animatingSelectionCount = data.animatingSelectionCount || false

    // Custom logic
    if (this.type === FilterTypes.TAX)
      this.labelKey = "_tax_name"
    if (this.type === FilterTypes.STAFF)
      this.labelKey = "_full_name"
    if (this.type === FilterTypes.UNIT)
      this.labelKey = "_unit_name"
    if (this.type === FilterTypes.STAFF)
      this.labelKey = "_fullName"
    if (this.type === FilterTypes.TOGGLE_BOX && !data.value)
      this.value = false
  }

  clear = () => {
    this.value = []
  }

  clearSearch = () => {
    this.searchKeyword = null
  }

  get isVeryCustom() {
    return this.type === FilterTypes.VERY_CUSTOM
  }

  get veryCustomSlotName() {
    return `${this.uuid}-very-custom`
  }

  get label() {
    if (this.customLabel)
      return this.customLabel
    switch (this.type) {
      case FilterTypes.BRAND:
        return i18n.t('generic.field.brand.s').toUpperCase()
      case FilterTypes.VENDOR:
        return i18n.t('generic.field.vendor.s').toUpperCase()
      case FilterTypes.BRANCH:
        return i18n.t('generic.filters.branch')
      case FilterTypes.TAG:
        return i18n.t('generic.field.tag.s').toUpperCase()
      case FilterTypes.CATEGORY:
        return i18n.t('generic.field.category.s').toUpperCase()
      case FilterTypes.TAX:
        return i18n.t('generic.filters.tax')
      case FilterTypes.STAFF:
        return i18n.t('generic.field.staff.s').toUpperCase()
      case FilterTypes.PAYMENT_TYPE:
        return i18n.t('generic.filters.payment_type')
      case FilterTypes.TERMINAL:
        return i18n.t('generic.field.terminal.s').toUpperCase()
      case FilterTypes.BRANCH_TREE:
        return i18n.t('generic.filters.branch_tree')
      case FilterTypes.MARKETPLACE:
        return i18n.t('generic.filters.marketplace_type')
      case FilterTypes.SALES_CHANNEL:
        return i18n.t('generic.filters.sales_channel').toUpperCase()
      case FilterTypes.UNIT:
        return i18n.t('generic.filters.unit').toUpperCase()
      case FilterTypes.REGISTRATION_SOURCE:
        return i18n.t('NEREDEN BULDU')
      case FilterTypes.ACTIVE_PLUGIN:
        return i18n.t('PAKETLER')
      case FilterTypes.INTEREST:
        return i18n.t('İLGİ ALANI')
      default:
        break;
    } 
  }

  get dataList() {
    switch (this.type) {
      case FilterTypes.BRAND:
        return store.state.Brand.filter_brand_list.list
      case FilterTypes.VENDOR:
        return store.state.Vendor.filter_vendor_list.list
      case FilterTypes.BRANCH:
        return store.state.Branch.filter_branch_list.list
      case FilterTypes.TAG:
        return store.state.Tag.filter_tag_list.list
      case FilterTypes.CATEGORY:
        return store.state.Category.category_list.list
      case FilterTypes.TAX:
        return store.state.Tax.tax_list.list
      case FilterTypes.STAFF:
        return store.state.Auth.user_list.list
      case FilterTypes.PAYMENT_TYPE:
        return store.state.PaymentType.payment_type_list.list
      case FilterTypes.SALES_CHANNEL:
        return store.state.App.sales_channel_list.list
      case FilterTypes.REGISTRATION_SOURCE:
        return store.state.App.registration_source_list
      case FilterTypes.ACTIVE_PLUGIN:
        return ActivePluginList
      case FilterTypes.INTEREST:
        return InterestList
      case FilterTypes.UNIT:
        return store.state.Unit.unit_list.list
      case FilterTypes.TERMINAL:
        return this.customTerminalDataList
      case FilterTypes.BRANCH_TREE:
        return this.customTerminalDataList
      case FilterTypes.CUSTOM:
        return this.customDataList
      default:
        break;
    }
  }

  get options() {
    switch (this.type) {
      case FilterTypes.BRAND:
      case FilterTypes.VENDOR:
      case FilterTypes.BRANCH:
      case FilterTypes.TAG:
      case FilterTypes.TAX:
      case FilterTypes.STAFF:
      case FilterTypes.PAYMENT_TYPE:
      case FilterTypes.TERMINAL:
      case FilterTypes.SALES_CHANNEL:
      case FilterTypes.MARKETPLACE:
      case FilterTypes.UNIT:
      case FilterTypes.REGISTRATION_SOURCE:
      case FilterTypes.ACTIVE_PLUGIN:
      case FilterTypes.INTEREST:
      case FilterTypes.BRANCH_TREE:
        return this._createOptions()
      case FilterTypes.CUSTOM:
        return this._createOptions()
      case FilterTypes.CATEGORY:
        return this.dataList
      default:
        break;
    }
  }

  get selectedItemCount() {
    return this.value ? this.value.length : 0
  }

  get selectedItemsLabel() {
    switch (this.type) {
      case FilterTypes.BRAND:
      case FilterTypes.VENDOR:
      case FilterTypes.BRANCH:
      case FilterTypes.TAG:
      case FilterTypes.TAX:
      case FilterTypes.STAFF:
      case FilterTypes.UNIT:
      case FilterTypes.PAYMENT_TYPE:
      case FilterTypes.TERMINAL:
      case FilterTypes.SALES_CHANNEL:
      case FilterTypes.MARKETPLACE:
      case FilterTypes.REGISTRATION_SOURCE:
      case FilterTypes.ACTIVE_PLUGIN:
      case FilterTypes.INTEREST:
      case FilterTypes.BRANCH_TREE:
        return this._getSelectedItemsLabel(this.dataList, this.value)
      case FilterTypes.CUSTOM:
        return this._getSelectedItemsLabel(this.dataList, this.value)
      case FilterTypes.CATEGORY:
        const flatCategories = store.state.Category.category_list.flat
        return this._getSelectedItemsLabel(flatCategories, this._filterParentCategories())
      default:
        break;
    }
  }

  get hasSelectedFilter() {
    return this.value && this.value.length
  }

  get valueStr() {
    if (this.type === FilterTypes.TOGGLE_BOX)
      return this.value
    return this.value.join(',')
  }

  _createOptions = () => {
    let options = []
    this.dataList.forEach(item => {
      options.push({
        [this.idKey]: item[this.idKey],
        [this.labelKey]: item[this.labelKey],
        [this.selectKey]: this.value.includes(item.id)
      })
    })
    return options
  }

  _getSelectedItemsLabel = (list, value) => {
    let label = list.reduce((prev, current, index) => {
      if (!value.includes(current[this.idKey]))
        return prev
      return prev + current[this.labelKey] + ", "
    }, "")
    return label.substring(0, label.length - 2)
  }

  _filterParentCategories = () => {
    const flatCategories = store.state.Category.category_list.flat
    return this.value.filter((categoryId, index) => {
      const category = flatCategories.find(c => c.id === categoryId)
      const parentCategory = category.$parent
      return !parentCategory || !this.value.includes(parentCategory.id)
    })
  }
}

export class FilterFactory {
  constructor(data = {}) {
    this.filterTypes = data.filterTypes || []
    this.initialValues = data.initialValues || {}

    // Private
    this._filters = []
    this._createFilters()
  }

  _createFilters() {
    this.filterTypes.forEach(filterType => {
      this._createFilter(filterType)
    })
  }

  _createFilter(filterType) {
    let filter = null
    let backendKey = null

    if (_isObject(filterType)) {
      backendKey = filterType.backendKey || getDefaultBackendKey(filterType.type)
      filter = new Filter(filterType)
    } 
    else {
      backendKey = getDefaultBackendKey(filterType)
      filter = new Filter({type: filterType})
    }

    filter.backendKey = backendKey

    if (this.initialValues && this.initialValues[backendKey] !== undefined) {
      if (this.initialValues[backendKey] === null) {
        if (filter.type === FilterTypes.TOGGLE_BOX)
          filter.value = false
        else
          filter.value = []
      }
      else {
        if (filter.type === FilterTypes.TOGGLE_BOX)
          filter.value = this.initialValues[backendKey]
        else
          filter.value = ('' + this.initialValues[backendKey]).split(',').map(n => parseInt(n))
      }
    }

    this._filters.push(filter)
  }

  get filterValues() {
    return this._filters.reduce((allValues, filter) => ({
      ...allValues, 
      [filter.backendKey]: filter.valueStr || null
    }), {})
  }

  get filters() {
    return this._filters
  }

}

function getDefaultBackendKey(filterType) {
  switch (filterType) {
    case FilterTypes.BRANCH:
      return "branch"
    case FilterTypes.BRAND:
      return "brand"
    case FilterTypes.VENDOR:
      return "vendor"
    case FilterTypes.TAG:
      return "tag"
    case FilterTypes.CATEGORY:
      return "category"
    case FilterTypes.TAX:
      return "tax"
    case FilterTypes.STAFF:
      return "staff"
    case FilterTypes.PAYMENT_TYPE:
      return "payment_type"
    case FilterTypes.TERMINAL:
      return "terminal"
    case FilterTypes.SALES_CHANNEL:
      return "sales_channel"
    case FilterTypes.MARKETPLACE:
      return "marketplace"
    case FilterTypes.UNIT:
      return "unit"
    case FilterTypes.BRANCH_TREE:
      return "terminal"
    case FilterTypes.REGISTRATION_SOURCE:
      return "registration_source_id"
    case FilterTypes.ACTIVE_PLUGIN:
      return "active_plugin"
    case FilterTypes.INTEREST:
      return "interest"
    default:
      break;
  }
}